import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Badge, Button, Card, Col, Container, Form, Modal, Row, Spinner, Table } from 'react-bootstrap';
import { useAuth } from '../../components/auth/AuthProvider';
import { replaceDocumentFile, updateDocumentMeta, uploadDocument } from '../../controllers/documents';
import { getAdminServices, getServices, updateService, createService, uploadServiceImage } from '../../controllers/services';

function ServiceDocumentsTable({ service, onEditDoc, onToggleDoc, onReplaceDoc, onAddDoc }) {
  return (
    <>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <strong>Documentos</strong>
        <Button size='sm' onClick={() => onAddDoc(service)} disabled={(service.documents || []).filter(d => d.active).length >= 2}>
          + Agregar documento
        </Button>
      </div>
      <Table hover responsive size='sm'>
        <thead>
          <tr>
            <th>Titulo</th>
            <th>Archivo</th>
            <th>Estado</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          {(service.documents || []).length === 0 && (
            <tr>
              <td colSpan={4} className='text-muted text-center'>Sin documentos.</td>
            </tr>
          )}
          {(service.documents || []).map((doc) => (
            <tr key={doc.id}>
              <td>
                <div>{doc.title}</div>
                {doc.description && <div className='small text-muted'>{doc.description}</div>}
              </td>
              <td>
                <a href={doc.url} target='_blank' rel='noreferrer' className='small'>{doc.fileName}</a>
              </td>
              <td>
                <Badge bg={doc.active ? 'success' : 'secondary'}>{doc.active ? 'Activo' : 'Inactivo'}</Badge>
              </td>
              <td className='d-flex gap-1 flex-wrap'>
                <Button size='sm' variant='outline-secondary' onClick={() => onEditDoc(service, doc)}>Editar</Button>
                <Button size='sm' variant='outline-primary' onClick={() => onReplaceDoc(doc)}>Reemplazar archivo</Button>
                <Button size='sm' variant={doc.active ? 'outline-warning' : 'outline-success'} onClick={() => onToggleDoc(doc)}>
                  {doc.active ? 'Desactivar' : 'Activar'}
                </Button>
              </td>
            </tr>
          ))}
        </tbody>
      </Table>
    </>
  );
}

function ServicesPage() {
  const { token, hasPermission } = useAuth();
  const isAdmin = hasPermission('servicios_admin');
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [editingService, setEditingService] = useState(null);
  const [serviceSaving, setServiceSaving] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [newService, setNewService] = useState({ label: '', subtitle: '', content: '', sortOrder: 0, active: true });
  const [createSaving, setCreateSaving] = useState(false);

  const [showDocModal, setShowDocModal] = useState(false);
  const [editingDoc, setEditingDoc] = useState(null);
  const [selectedService, setSelectedService] = useState(null);
  const [docTitle, setDocTitle] = useState('');
  const [docDescription, setDocDescription] = useState('');
  const [docFile, setDocFile] = useState(null);
  const [docSaving, setDocSaving] = useState(false);
  const docFileRef = useRef();

  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [replaceDoc, setReplaceDoc] = useState(null);
  const [replaceFile, setReplaceFile] = useState(null);
  const [replaceSaving, setReplaceSaving] = useState(false);
  const replaceFileRef = useRef();

  const [imageFiles, setImageFiles] = useState({}); // keyed by serviceId
  const [imageSaving, setImageSaving] = useState(null); // serviceId being saved
  const imageFileRefs = useRef({});

  const visibleServices = useMemo(() => {
    if (isAdmin) return services;
    return services.filter(service => service.active);
  }, [services, isAdmin]);

  useEffect(() => {
    document.title = 'FAMA - Administracion de Servicios';
    loadServices();
  }, [token, isAdmin]);

  async function loadServices() {
    setLoading(true);
    setError(null);
    try {
      const data = isAdmin ? await getAdminServices(token) : await getServices();
      setServices(data);
    } catch (e) {
      setError('No se pudieron cargar los servicios.');
    } finally {
      setLoading(false);
    }
  }

  function setEditService(service) {
    setEditingService({
      id: service.id,
      label: service.label,
      subtitle: service.subtitle || '',
      content: service.content || '',
      sortOrder: service.sortOrder || 0,
      active: service.active
    });
  }

  async function saveService(e) {
    e.preventDefault();
    if (!editingService || !editingService.label.trim()) return;
    setServiceSaving(true);
    try {
      await updateService(token, editingService.id, {
        label: editingService.label,
        subtitle: editingService.subtitle,
        content: editingService.content,
        sortOrder: editingService.sortOrder,
        active: editingService.active
      });
      setSuccess('Servicio actualizado correctamente.');
      setEditingService(null);
      await loadServices();
    } catch (e2) {
      setError(e2.message || 'No se pudo guardar el servicio.');
    } finally {
      setServiceSaving(false);
    }
  }

  function openCreateDoc(service) {
    setEditingDoc(null);
    setSelectedService(service);
    setDocTitle('');
    setDocDescription('');
    setDocFile(null);
    if (docFileRef.current) docFileRef.current.value = '';
    setShowDocModal(true);
  }

  function openEditDoc(service, doc) {
    setEditingDoc(doc);
    setSelectedService(service);
    setDocTitle(doc.title || '');
    setDocDescription(doc.description || '');
    setDocFile(null);
    if (docFileRef.current) docFileRef.current.value = '';
    setShowDocModal(true);
  }

  async function saveDoc(e) {
    e.preventDefault();
    if (!selectedService || !docTitle.trim()) return;
    setDocSaving(true);
    try {
      if (editingDoc) {
        await updateDocumentMeta(token, editingDoc.id, {
          title: docTitle,
          description: docDescription,
          groupId: selectedService.id
        });
        setSuccess('Documento actualizado.');
      } else {
        if (!docFile) {
          setError('Debe seleccionar un archivo para el documento.');
          setDocSaving(false);
          return;
        }
        await uploadDocument(token, selectedService.id, docTitle, docDescription, docFile);
        setSuccess('Documento agregado.');
      }
      setShowDocModal(false);
      await loadServices();
    } catch (e2) {
      setError(e2.message || 'No se pudo guardar el documento.');
    } finally {
      setDocSaving(false);
    }
  }

  async function toggleDocActive(doc) {
    try {
      await updateDocumentMeta(token, doc.id, { active: !doc.active });
      setSuccess(`Documento ${doc.active ? 'desactivado' : 'activado'}.`);
      await loadServices();
    } catch (e) {
      setError(e.message || 'No se pudo actualizar el documento.');
    }
  }

  function openReplaceDoc(doc) {
    setReplaceDoc(doc);
    setReplaceFile(null);
    if (replaceFileRef.current) replaceFileRef.current.value = '';
    setShowReplaceModal(true);
  }

  async function replaceDocFile(e) {
    e.preventDefault();
    if (!replaceDoc || !replaceFile) return;
    setReplaceSaving(true);
    try {
      await replaceDocumentFile(token, replaceDoc.id, replaceFile);
      setSuccess('Archivo reemplazado correctamente.');
      setShowReplaceModal(false);
      await loadServices();
    } catch (e2) {
      setError(e2.message || 'No se pudo reemplazar el archivo.');
    } finally {
      setReplaceSaving(false);
    }
  }

  async function saveServiceImage(service) {
    const file = imageFiles[service.id];
    if (!file) return;
    setImageSaving(service.id);
    try {
      await uploadServiceImage(token, service.id, file);
      setSuccess('Imagen actualizada correctamente.');
      setImageFiles(prev => { const n = { ...prev }; delete n[service.id]; return n; });
      if (imageFileRefs.current[service.id]) imageFileRefs.current[service.id].value = '';
      await loadServices();
    } catch (e2) {
      setError(e2.message || 'No se pudo actualizar la imagen.');
    } finally {
      setImageSaving(null);
    }
  }

  function openCreateService() {
    setNewService({ label: '', subtitle: '', content: '', sortOrder: 0, active: true });
    setShowCreateModal(true);
  }

  async function handleCreateService(e) {
    e.preventDefault();
    if (!newService.label.trim()) return;
    setCreateSaving(true);
    try {
      await createService(token, newService);
      setSuccess('Servicio creado correctamente.');
      setShowCreateModal(false);
      await loadServices();
    } catch (e2) {
      setError(e2.message || 'No se pudo crear el servicio.');
    } finally {
      setCreateSaving(false);
    }
  }

  return (
    <Container className='public-page-container'>
      <div className='d-flex justify-content-between align-items-center mb-4'>
        <h1 className='mb-0'>Servicios</h1>
        <div className='d-flex gap-2 align-items-center'>
          {isAdmin && <Button variant='primary' size='sm' onClick={openCreateService}>+ Nuevo servicio</Button>}
          {isAdmin && <Badge bg='dark'>Modo Administrador</Badge>}
        </div>
      </div>

      {loading && <div className='text-center py-5'><Spinner animation='border' /></div>}
      {error && <Alert variant='danger' dismissible onClose={() => setError(null)}>{error}</Alert>}
      {success && <Alert variant='success' dismissible onClose={() => setSuccess(null)}>{success}</Alert>}

      {!loading && visibleServices.length === 0 && (
        <Alert variant='info'>No hay servicios disponibles.</Alert>
      )}

      {!loading && visibleServices.map(service => (
        <Card key={service.id} className='mb-4'>
          <Card.Body>
            {!isAdmin && (
              <>
                <h4>{service.label}</h4>
                {service.subtitle && <p className='text-muted'>{service.subtitle}</p>}
                {service.content && <p>{service.content}</p>}
              </>
            )}

            {isAdmin && (
              <>
                {editingService?.id === service.id ? (
                  <Form onSubmit={saveService}>
                    <Row>
                      <Col md={8}>
                        <Form.Group className='mb-3'>
                          <Form.Label>Titulo</Form.Label>
                          <Form.Control
                            value={editingService.label}
                            onChange={e => setEditingService(prev => ({ ...prev, label: e.target.value }))}
                            required
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className='mb-3'>
                          <Form.Label>Orden</Form.Label>
                          <Form.Control
                            type='number'
                            min={0}
                            value={editingService.sortOrder}
                            onChange={e => setEditingService(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                          />
                        </Form.Group>
                      </Col>
                      <Col md={2}>
                        <Form.Group className='mb-3'>
                          <Form.Label>Activo</Form.Label>
                          <Form.Check
                            type='switch'
                            checked={editingService.active}
                            onChange={e => setEditingService(prev => ({ ...prev, active: e.target.checked }))}
                            label={editingService.active ? 'Si' : 'No'}
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                    <Form.Group className='mb-3'>
                      <Form.Label>Subtitulo</Form.Label>
                      <Form.Control
                        value={editingService.subtitle}
                        onChange={e => setEditingService(prev => ({ ...prev, subtitle: e.target.value }))}
                      />
                    </Form.Group>
                    <Form.Group className='mb-3'>
                      <Form.Label>Contenido</Form.Label>
                      <Form.Control
                        as='textarea'
                        rows={7}
                        value={editingService.content}
                        onChange={e => setEditingService(prev => ({ ...prev, content: e.target.value }))}
                      />
                      <Form.Text className='text-muted'>Separa parrafos con una linea en blanco.</Form.Text>
                    </Form.Group>
                    <div className='d-flex gap-2'>
                      <Button type='submit' disabled={serviceSaving}>
                        {serviceSaving ? <Spinner size='sm' animation='border' /> : 'Guardar'}
                      </Button>
                      <Button variant='outline-secondary' onClick={() => setEditingService(null)}>Cancelar</Button>
                    </div>
                  </Form>
                ) : (
                  <div className='d-flex justify-content-between align-items-start'>
                    <div>
                      <h4>{service.label}</h4>
                      <p className='text-muted mb-1'>{service.subtitle || 'Sin subtitulo'}</p>
                      <p className='small text-muted mb-0'>Orden: {service.sortOrder || 0}</p>
                    </div>
                    <div className='d-flex gap-2'>
                      <Badge bg={service.active ? 'success' : 'secondary'}>{service.active ? 'Activo' : 'Inactivo'}</Badge>
                      <Button size='sm' variant='outline-primary' onClick={() => setEditService(service)}>Editar contenido</Button>
                    </div>
                  </div>
                )}

                <hr />
                <ServiceDocumentsTable
                  service={service}
                  onAddDoc={openCreateDoc}
                  onEditDoc={openEditDoc}
                  onToggleDoc={toggleDocActive}
                  onReplaceDoc={openReplaceDoc}
                />

                <hr />
                <div className='mb-2'><strong>Imagen del servicio</strong></div>
                <Row className='align-items-center g-2 mb-2'>
                  {service.imageUrl && (
                    <Col xs='auto'>
                      <img src={service.imageUrl} alt={service.label} style={{ maxHeight: 80, maxWidth: 160, objectFit: 'contain', borderRadius: 4 }} />
                    </Col>
                  )}
                  <Col>
                    <Form.Control
                      type='file'
                      accept='image/png,image/jpeg,image/webp,image/gif,image/svg+xml'
                      ref={el => { imageFileRefs.current[service.id] = el; }}
                      onChange={e => setImageFiles(prev => ({ ...prev, [service.id]: e.target.files?.[0] || undefined }))}
                    />
                    <Form.Text className='text-muted'>PNG, JPG, WEBP, GIF o SVG. Max 5 MB.</Form.Text>
                  </Col>
                  <Col xs='auto'>
                    <Button
                      size='sm'
                      disabled={imageSaving === service.id || !imageFiles[service.id]}
                      onClick={() => saveServiceImage(service)}
                    >
                      {imageSaving === service.id ? <Spinner size='sm' animation='border' /> : 'Subir imagen'}
                    </Button>
                  </Col>
                </Row>
              </>
            )}
          </Card.Body>
        </Card>
      ))}

      <Modal show={showDocModal} onHide={() => setShowDocModal(false)}>
        <Form onSubmit={saveDoc}>
          <Modal.Header closeButton>
            <Modal.Title>{editingDoc ? 'Editar documento' : 'Agregar documento'}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className='text-muted small'>Servicio: {selectedService?.label}</p>
            <Form.Group className='mb-3'>
              <Form.Label>Titulo del documento</Form.Label>
              <Form.Control value={docTitle} onChange={e => setDocTitle(e.target.value)} required />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Descripcion</Form.Label>
              <Form.Control as='textarea' rows={2} value={docDescription} onChange={e => setDocDescription(e.target.value)} />
            </Form.Group>
            {!editingDoc && (
              <Form.Group>
                <Form.Label>Archivo</Form.Label>
                <Form.Control
                  type='file'
                  accept='.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.ppt,.pptx'
                  ref={docFileRef}
                  required
                  onChange={e => setDocFile(e.target.files?.[0] || null)}
                />
                <Form.Text className='text-muted'>
                  Formatos permitidos: PDF, imágenes, Word, Excel y PowerPoint.
                </Form.Text>
              </Form.Group>
            )}
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowDocModal(false)}>Cancelar</Button>
            <Button type='submit' disabled={docSaving}>
              {docSaving ? <Spinner size='sm' animation='border' /> : 'Guardar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showReplaceModal} onHide={() => setShowReplaceModal(false)}>
        <Form onSubmit={replaceDocFile}>
          <Modal.Header closeButton>
            <Modal.Title>Reemplazar archivo</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <p className='small text-muted mb-1'>Documento: {replaceDoc?.title}</p>
            <p className='small text-muted'>Archivo actual: {replaceDoc?.fileName}</p>
            <Form.Group>
              <Form.Label>Nuevo archivo</Form.Label>
              <Form.Control
                type='file'
                accept='.pdf,.png,.jpg,.jpeg,.doc,.docx,.xls,.xlsx,.ppt,.pptx'
                required
                ref={replaceFileRef}
                onChange={e => setReplaceFile(e.target.files?.[0] || null)}
              />
              <Form.Text className='text-muted'>
                Formatos permitidos: PDF, imágenes, Word, Excel y PowerPoint.
              </Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowReplaceModal(false)}>Cancelar</Button>
            <Button variant='warning' type='submit' disabled={replaceSaving}>
              {replaceSaving ? <Spinner size='sm' animation='border' /> : 'Reemplazar'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)}>
        <Form onSubmit={handleCreateService}>
          <Modal.Header closeButton>
            <Modal.Title>Nuevo servicio</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Row>
              <Col md={8}>
                <Form.Group className='mb-3'>
                  <Form.Label>Título <span className='text-danger'>*</span></Form.Label>
                  <Form.Control
                    value={newService.label}
                    onChange={e => setNewService(prev => ({ ...prev, label: e.target.value }))}
                    required
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className='mb-3'>
                  <Form.Label>Orden</Form.Label>
                  <Form.Control
                    type='number'
                    min={0}
                    value={newService.sortOrder}
                    onChange={e => setNewService(prev => ({ ...prev, sortOrder: parseInt(e.target.value) || 0 }))}
                  />
                </Form.Group>
              </Col>
              <Col md={2}>
                <Form.Group className='mb-3'>
                  <Form.Label>Activo</Form.Label>
                  <Form.Check
                    type='switch'
                    checked={newService.active}
                    onChange={e => setNewService(prev => ({ ...prev, active: e.target.checked }))}
                    label={newService.active ? 'Sí' : 'No'}
                  />
                </Form.Group>
              </Col>
            </Row>
            <Form.Group className='mb-3'>
              <Form.Label>Subtítulo</Form.Label>
              <Form.Control
                value={newService.subtitle}
                onChange={e => setNewService(prev => ({ ...prev, subtitle: e.target.value }))}
              />
            </Form.Group>
            <Form.Group className='mb-3'>
              <Form.Label>Contenido</Form.Label>
              <Form.Control
                as='textarea'
                rows={5}
                value={newService.content}
                onChange={e => setNewService(prev => ({ ...prev, content: e.target.value }))}
              />
              <Form.Text className='text-muted'>Separa párrafos con una línea en blanco.</Form.Text>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant='secondary' onClick={() => setShowCreateModal(false)}>Cancelar</Button>
            <Button type='submit' disabled={createSaving}>
              {createSaving ? <Spinner size='sm' animation='border' /> : 'Crear servicio'}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </Container>
  );
}

export default ServicesPage;
